import { createSlice, createSelector } from "@reduxjs/toolkit";
import { getCurrenciesNameAction, getTickersAction } from "../../actions";

const initialState = {
  settings: {},
  external: {
    colorBlindMode: false,
    enableColorBlindMode: false,
    currenciesName: {},
  },
};
const settingSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setSettings: (state, { payload }) => {
      payload.currencies = payload.currencies
        .map(c => ({
          ...c,
          order: c.order || 0,
          text: c.text || c.code,
        }))
        .sort((a, b) => {
          return a.order - b.order;
        });
      state.settings = payload;
      state.external.enableColorBlindMode = Boolean(payload.enabledColorBlindMode)
      state.external.colorBlindMode = false;
    },
    setColorBlind: (state, /** @type {{payload: boolean}} */ { payload }) => {
      state.external.colorBlindMode = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(getCurrenciesNameAction.fulfilled, (state, { payload }) => {
      state.external.currenciesName = payload.data.currency.currenciesByCodes.reduce(
        (acc, item) => {
          acc[item.code] = item.name;
          return acc;
        },
        { ...state.external.currenciesName }
      );
    });
    builder.addCase(getTickersAction.fulfilled, (state, { payload }) => {
      state.external.currenciesName = payload.data.instruments.reduce(
        (acc, item) => {
          acc[item.currencyCode] = item.currency;
          return acc;
        },
        { ...state.external.currenciesName }
      );
    });
  },
});

export default settingSlice;
/**
 * @param {} state
 * @returns { import("../../..").CompanySetting }
 */
export const companySettingsSelector = state => state.companySettings.settings;

/**
 * @param {} state
 * @returns { import("../../..").IInstrument[] }
 */
export const instrumentsSettingsSelector = state => companySettingsSelector(state).instruments;

export const selectedInstrumentsSettingsSelectorWrapper = () =>
  createSelector(
    [companySettingsSelector, instrumentsSettingsSelector, state => state.trade.selectedInstrumentId],
    (companySettings, instruments, selectedInstrumentId) => {
      let selectedInstrumentSetting = instruments.find(ins => String(ins.id) === String(selectedInstrumentId)) || {};

      if (typeof selectedInstrumentSetting.enabledDividendOption !== "boolean") {
        selectedInstrumentSetting = {
          ...selectedInstrumentSetting,
          enabledDividendOption: companySettings.enabledDividendOption,
        };
      }
      return selectedInstrumentSetting;
    }
  );

/**
 * @param {} state
 * @returns { import("../../..").TTemplate }
 */
export const templateSettingsSelector = state => companySettingsSelector(state).template;

/**
 * @param {} state
 * @returns { import("../../..").IFormatSetting }
 */
export const formatSettingsSelector = state => companySettingsSelector(state).format;

export const instrumentSettingsIndexer = createSelector(
  [state => companySettingsSelector(state).instruments, state => companySettingsSelector(state).peers],
  (instruments, peers) => {
    return [...instruments, ...(peers || [])].reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, /** @type { Record<number, IPeerSetting | IInstrumentSetting>} */ ({}));
  }
);

export const currenciesSelector = state => companySettingsSelector(state).currencies;

export const currenciesSettingIndexerSelector = createSelector(currenciesSelector, currency => {
  return currency.reduce((acc, item) => {
    acc[item.code] = item.text;
    return acc;
  }, /** @type { Record<string, string>} */ ({}));
});

export const companyExternalSettingsSelector = state => state.companySettings.external;

export const colorBlindModeSelector = state => companyExternalSettingsSelector(state).colorBlindMode;

export const enableColorBlindModeSelector = (state) => companyExternalSettingsSelector(state).enableColorBlindMode;

export const currenciesNameSelector = state => companyExternalSettingsSelector(state).currenciesName;

export const { setSettings, setColorBlind } = settingSlice.actions;
